import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Center,
  IconButton,
  Flex,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowDown, ShieldAlert, UserPlus } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { outboxAtom, pendingUploadsAtom } from '../../store';
import { CallSummary, callMetadata } from './CallSummary';
import { MessageBubble, type MessageActions } from './MessageBubble';
import { PendingBand } from './PendingBand';
import { NewDeviceBanner } from './NewDeviceBanner';
import {
  formatDayLabel,
  isSameDay,
  placeDeviceNotices,
  type ChatMessage,
  type DeviceNotice,
  type UnreadMarker,
} from './messageUtils';
import { useT } from '../../i18n';
import { useLoadingState } from '../../hooks/useLoadingState';
import { useKeyStorageState } from '../../hooks/useE2EE';
import { LoadingPanel, TwindenLoader } from '../brand/TwindenLoader';
import { systemMessageText } from '../../lib/deletedAccounts';

// Consecutive messages from one sender within this window form a run.
const RUN_GAP_MS = 5 * 60 * 1000;
// How close to the bottom still counts as "following along".
const NEAR_BOTTOM_PX = 120;
// Jump to latest (D271): further than this many screens is an instant jump
// (then a short smooth settle) rather than a long smooth scroll.
const INSTANT_JUMP_SCREENS = 3;
// The top of the timeline holds this height whether it shows the older-page
// mark or Try again, so swapping one for the other never shifts the rows.
const TOP_AREA_PX = 72;
// The button's smooth scroll ends well within this; a backstop only.
const SETTLE_MS = 2000;

export interface MessageListHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Centers a loaded message and flashes it (reply quotes, search). */
  scrollToMessage: (messageId: string) => boolean;
  /**
   * As the ↓ button (D271): to the bottom, or the newest page when the
   * window is far back. A tapped notification for the open chat (#75).
   */
  jumpToLatest: () => Promise<void>;
}

interface MessageListProps {
  conversationId: string;
  /** The chat's family: senders' faces open through it (D292). */
  familyId: string;
  messages: ChatMessage[];
  currentUserId: string | undefined;
  hasMore: boolean;
  /** The first page is still on its way and nothing is shown (M66, D248). */
  loading?: boolean;
  /** The first page failed; Try again calls `onRetry` (D249). */
  loadError?: unknown;
  onRetry?: () => void;
  loadingOlder: boolean;
  /** Older pages are being fetched behind the first unread (D229, D250). */
  searchingUnread?: boolean;
  /** A window around a pinned or found message is being fetched (D250). */
  jumping?: boolean;
  onLoadOlder: () => void;
  /** An older page failed: Try again at the top (D270, D249). */
  olderError?: unknown;
  onRetryOlder?: () => void;
  /** The window stops short of the newest message (D271). */
  hasNewer?: boolean;
  /** Messages from others that arrived while `hasNewer` (D271). */
  newerCount?: number;
  /** Loads the newest page when `hasNewer` (D271). */
  onLoadLatest?: () => Promise<boolean>;
  loadingLatest?: boolean;
  isGroup: boolean;
  otherParticipantIds: string[];
  /** user id → handle, for @mentions (D27). */
  handleById: ReadonlyMap<string, string>;
  actions: MessageActions;
  /** "N unread messages" above the first unread one (M62, D229). */
  unreadMarker?: UnreadMarker | null;
  /** "{Name}'s devices changed" lines, this device only (M73, D303). */
  deviceNotices?: readonly DeviceNotice[];
  /** A device notice was tapped: show that person's security code. */
  onDeviceNotice?: (userId: string) => void;
}

const NO_NOTICES: readonly DeviceNotice[] = [];

function startsRun(previous: ChatMessage | undefined, message: ChatMessage) {
  if (!previous || previous.type === 'system' || message.type === 'system') {
    return true;
  }
  if (previous.sender?.id !== message.sender?.id) return true;
  const gap =
    new Date(message.createdAt).getTime() -
    new Date(previous.createdAt).getTime();
  return gap > RUN_GAP_MS;
}

export const MessageList = forwardRef<MessageListHandle, MessageListProps>(
  function MessageList(
    {
      conversationId,
      familyId,
      messages,
      currentUserId,
      hasMore,
      loading = false,
      loadError = null,
      onRetry,
      loadingOlder,
      searchingUnread = false,
      jumping = false,
      onLoadOlder,
      olderError = null,
      onRetryOlder,
      hasNewer = false,
      newerCount = 0,
      onLoadLatest,
      loadingLatest = false,
      isGroup,
      otherParticipantIds,
      handleById,
      actions,
      unreadMarker = null,
      deviceNotices = NO_NOTICES,
      onDeviceNotice,
    },
    ref
  ) {
    const subtle = useColorModeValue('gray.500', 'gray.400');
    const t = useT();
    // The mark while the first page loads with nothing on screen; messages
    // already here (from the socket, an earlier open) show at once (D248).
    const firstPage = useLoadingState({
      loading,
      hasData: messages.length > 0,
      error: loadError,
      retry: onRetry,
    });
    // The small mark at the top for older pages, the unread search and
    // jumps, after 150 ms like everything else (D250).
    const topWait = useLoadingState({
      loading: loadingOlder || searchingUnread || jumping,
    });
    // An older page that failed: the inline Try again (D270, D249).
    const olderFailed = useLoadingState({
      loading: false,
      error: olderError,
      retry: onRetryOlder,
    });
    const dayChipBg = useColorModeValue('gray.100', 'gray.700');
    // Device notices among the messages (D303): local lines, not messages.
    const notices = placeDeviceNotices(messages, deviceNotices, {
      hasMore,
      hasNewer,
    });
    const noticeLine = (notice: DeviceNotice) => {
      if (notice.kind === 'added' || notice.kind === 'joined') {
        // Someone joined after this device first looked (D343): quiet, like
        // a system line (D259), and nothing to open.
        return (
          <Flex
            key={`${notice.kind}-${notice.userId}`}
            data-testid="member-join-notice"
            justify="center"
            py={1}
          >
            <Flex
              align="center"
              gap={1}
              fontSize="xs"
              color={subtle}
              textAlign="center"
              px={2}
              py={1}
            >
              <UserPlus size={14} aria-hidden="true" />
              <span>
                {t(
                  notice.kind === 'joined'
                    ? 'chat.memberJoinedFamily'
                    : 'chat.memberAdded',
                  { name: notice.name }
                )}
              </span>
            </Flex>
          </Flex>
        );
      }
      const label = t('chat.devicesChanged', { name: notice.name });
      return (
        <Flex
          key={`devices-${notice.userId}`}
          data-testid="device-notice"
          justify="center"
          py={1}
        >
          {/* Quiet like a system line (D259), but it opens the code. */}
          <Box
            as="button"
            type="button"
            onClick={() => onDeviceNotice?.(notice.userId)}
            display="inline-flex"
            alignItems="center"
            gap={1}
            fontSize="xs"
            color={subtle}
            textAlign="center"
            px={2}
            py={1}
            minH="32px"
            borderRadius="full"
            _hover={{ color: 'accent', textDecoration: 'underline' }}
            _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
          >
            <ShieldAlert size={14} aria-hidden="true" />
            <span>{label}</span>
          </Box>
        </Flex>
      );
    };
    // In dark mode surface.subtle is surface.raised's colour: use the line.
    const pressedBg = useColorModeValue('surface.subtle', 'line');
    // Text still on its way (M23), minus anything that already arrived.
    const deliveredClientIds = new Set(
      messages.flatMap((message) =>
        message.clientId ? [message.clientId] : []
      )
    );
    // An upload's id is its clientId; its message:sent (D206) can land
    // before the send returns.
    const pendingUploads = useAtomValue(pendingUploadsAtom).filter(
      (upload) =>
        upload.conversationId === conversationId &&
        !deliveredClientIds.has(upload.id)
    );
    // Only new messages get a pending row; a waiting edit or reaction
    // already shows on its message (M29, D69).
    const outbox = useAtomValue(outboxAtom).filter(
      (entry) =>
        (entry.kind ?? 'send') === 'send' &&
        entry.conversationId === conversationId &&
        !deliveredClientIds.has(entry.clientId)
    );

    // Scroll-follow (WhatsApp-style): stay pinned to the newest message when
    // the reader is already at the bottom; otherwise count arrivals and offer
    // the jump-to-latest button (D271) instead of yanking them away from what
    // they are reading.
    const listRef = useRef<HTMLDivElement>(null);
    const nearBottomRef = useRef(true);
    const [newMessageCount, setNewMessageCount] = useState(0);
    // More than a screen from the bottom: the button shows (D271).
    const [farFromBottom, setFarFromBottom] = useState(false);
    // While the button's own scroll is on its way down, the button stays
    // hidden rather than flicker back with each scroll event (D271).
    const settlingRef = useRef(false);
    const measureDistance = useCallback(() => {
      const list = listRef.current;
      if (!list) return;
      const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
      setFarFromBottom(!settlingRef.current && distance > list.clientHeight);
    }, []);
    // Set by the button while the newest page loads (D271), so the window
    // that replaces this one opens at its bottom.
    const followLatestRef = useRef(false);

    // What the list looked like after the previous render, to tell an
    // appended message from prepended history.
    const renderedRef = useRef<{
      conversationId: string | null;
      firstId: string | null;
      lastId: string | null;
      scrollHeight: number;
      hasNewer: boolean;
    }>({
      conversationId: null,
      firstId: null,
      lastId: null,
      scrollHeight: 0,
      hasNewer: false,
    });

    const scrollToBottom = useCallback(
      (behavior: ScrollBehavior = 'smooth') => {
        const list = listRef.current;
        if (!list) return;
        list.scrollTo({ top: list.scrollHeight, behavior });
      },
      []
    );

    // Puts the unread divider near the top of the view (D229). While the
    // reader hasn't scrolled yet, photos loading above keep it there.
    const unreadAnchoredRef = useRef(false);
    const scrollToUnread = useCallback(() => {
      const list = listRef.current;
      const divider = list?.querySelector('#unread-divider');
      if (!list || !divider) return false;
      const offset =
        divider.getBoundingClientRect().top -
        list.getBoundingClientRect().top +
        list.scrollTop;
      list.scrollTop = Math.max(0, offset - 8);
      return true;
    }, []);
    // The reader took over the scroll: the unread anchor and the button's
    // settling scroll let go.
    const releaseUnreadAnchor = useCallback(() => {
      unreadAnchoredRef.current = false;
      settlingRef.current = false;
    }, []);

    const [highlightId, setHighlightId] = useState<string | null>(null);
    const scrollToMessage = useCallback((messageId: string) => {
      unreadAnchoredRef.current = false;
      const element = listRef.current?.querySelector(
        `#message-${CSS.escape(messageId)}`
      );
      const list = listRef.current;
      if (!element || !list) return false;
      // Scroll the list only: scrollIntoView also scrolls every scrollable
      // ancestor, including the page.
      const offset =
        element.getBoundingClientRect().top -
        list.getBoundingClientRect().top +
        list.scrollTop;
      list.scrollTo({
        top: offset - list.clientHeight / 2 + element.clientHeight / 2,
        behavior: 'smooth',
      });
      setHighlightId(messageId);
      return true;
    }, []);
    useEffect(() => {
      if (!highlightId) return;
      const timer = setTimeout(() => setHighlightId(null), 2000);
      return () => clearTimeout(timer);
    }, [highlightId]);

    // Declared below; the handle calls the latest one.
    const jumpToLatestRef = useRef<() => Promise<void>>(async () => undefined);
    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom,
        scrollToMessage,
        jumpToLatest: () => jumpToLatestRef.current(),
      }),
      [scrollToBottom, scrollToMessage]
    );

    const handleScroll = useCallback(() => {
      const list = listRef.current;
      if (!list) return;
      // Treat "close enough" as at the bottom so a smooth scroll in flight
      // does not immediately count as the reader having left.
      const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
      const near = distance < NEAR_BOTTOM_PX;
      nearBottomRef.current = near;
      if (near) {
        setNewMessageCount(0);
        settlingRef.current = false;
      }
      setFarFromBottom(!settlingRef.current && distance > list.clientHeight);
      renderedRef.current.scrollHeight = list.scrollHeight;
    }, []);

    // Media and layout changes alter the height after render; keep the newest
    // message visible if the reader was following the conversation.
    const handleMediaLoad = useCallback(() => {
      if (unreadAnchoredRef.current) scrollToUnread();
      else if (nearBottomRef.current) scrollToBottom('auto');
      if (listRef.current) {
        renderedRef.current.scrollHeight = listRef.current.scrollHeight;
      }
      measureDistance();
    }, [scrollToBottom, scrollToUnread, measureDistance]);

    // The visual viewport changes when the URL bar or the keyboard appears.
    useEffect(() => {
      const onViewportResize = () => {
        if (nearBottomRef.current) scrollToBottom('auto');
      };
      window.visualViewport?.addEventListener('resize', onViewportResize);
      window.addEventListener('resize', onViewportResize);
      return () => {
        window.visualViewport?.removeEventListener('resize', onViewportResize);
        window.removeEventListener('resize', onViewportResize);
      };
    }, [scrollToBottom]);

    // The list's height, for the sentinel's one-screen margin (D270).
    const [listHeight, setListHeight] = useState(0);
    // The list's own box shrinks without any viewport change when something
    // opens under it: the emoji panel with the keyboard down (M69, D263), the
    // reply bar, the album strip. A reader at the bottom stays there.
    useEffect(() => {
      const list = listRef.current;
      if (!list || typeof ResizeObserver === 'undefined') return;
      let height = list.clientHeight;
      const observer = new ResizeObserver(() => {
        const next = list.clientHeight;
        if (next === height) return;
        height = next;
        setListHeight(next);
        if (nearBottomRef.current && !unreadAnchoredRef.current) {
          scrollToBottom('auto');
        }
        measureDistance();
      });
      observer.observe(list);
      return () => observer.disconnect();
    }, [scrollToBottom, measureDistance]);

    // Runs before paint so a prepend never flashes at the wrong position.
    useLayoutEffect(() => {
      const list = listRef.current;
      const previous = renderedRef.current;
      const firstId = messages[0]?.id ?? null;
      const lastId = messages[messages.length - 1]?.id ?? null;

      const remember = () => {
        renderedRef.current = {
          conversationId,
          firstId,
          lastId,
          scrollHeight: list?.scrollHeight ?? 0,
          hasNewer,
        };
      };

      if (!list) {
        remember();
        return;
      }

      // Opening a conversation starts pinned to its newest message (the
      // unread divider, below, may move it).
      if (previous.conversationId !== conversationId) {
        nearBottomRef.current = true;
        setNewMessageCount(0);
        setFarFromBottom(false);
        // A jump-to-latest still loading for the last chat isn't this one's.
        followLatestRef.current = false;
        settlingRef.current = false;
        if (messages.length > 0) scrollToBottom('auto');
        // Until the first page arrives, keep treating this as a fresh open.
        renderedRef.current = {
          conversationId: messages.length > 0 ? conversationId : null,
          firstId,
          lastId,
          scrollHeight: list.scrollHeight,
          hasNewer,
        };
        return;
      }

      // A different window replaced this one: a jump far back, or the newest
      // page coming back after one (D271). Nothing was appended; coming back
      // to the newest opens at its bottom, as opening the chat does.
      const has = (id: string | null) =>
        id !== null && messages.some((message) => message.id === id);
      if (
        previous.lastId &&
        lastId &&
        !has(previous.lastId) &&
        !has(previous.firstId)
      ) {
        if (followLatestRef.current || (previous.hasNewer && !hasNewer)) {
          nearBottomRef.current = true;
          scrollToBottom('auto');
        } else {
          nearBottomRef.current = false;
        }
        followLatestRef.current = false;
        setNewMessageCount(0);
        remember();
        measureDistance();
        return;
      }

      // Older history was prepended: hold the reader's place.
      const prependedIndex = previous.firstId
        ? messages.findIndex((message) => message.id === previous.firstId)
        : -1;
      if (prependedIndex > 0) {
        list.scrollTop += list.scrollHeight - previous.scrollHeight;
      }

      // New messages were appended at the bottom.
      if (lastId && lastId !== previous.lastId) {
        const previousLastIndex = previous.lastId
          ? messages.findIndex((message) => message.id === previous.lastId)
          : -1;
        const appended = messages.slice(previousLastIndex + 1);
        const containsOwn =
          Boolean(currentUserId) &&
          appended.some((message) => message.sender?.id === currentUserId);

        if (nearBottomRef.current || containsOwn) {
          scrollToBottom(previous.lastId ? 'smooth' : 'auto');
          setNewMessageCount(0);
        } else {
          setNewMessageCount((count) => count + appended.length);
        }
      }

      remember();
      measureDistance();
    }, [
      messages,
      conversationId,
      currentUserId,
      scrollToBottom,
      hasNewer,
      measureDistance,
    ]);

    // The unread divider (D229): placed with the first page, or once the
    // search further back finds the first unread message. It moves the view
    // only while the reader is still where the chat opened; the pill then
    // counts the unread messages below.
    const placedRef = useRef<string | null>(null);
    useLayoutEffect(() => {
      if (!unreadMarker) return;
      const key = `${conversationId}:${unreadMarker.messageId}`;
      if (placedRef.current === key) return;
      const list = listRef.current;
      if (!list || renderedRef.current.conversationId !== conversationId) {
        return;
      }
      if (!nearBottomRef.current && !unreadAnchoredRef.current) {
        placedRef.current = key;
        return;
      }
      if (!scrollToUnread()) return;
      placedRef.current = key;
      unreadAnchoredRef.current = true;
      const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
      nearBottomRef.current = distance < NEAR_BOTTOM_PX;
      setNewMessageCount(nearBottomRef.current ? 0 : unreadMarker.count);
    }, [unreadMarker, conversationId, messages, scrollToUnread]);

    // Starting an upload is like sending: bring the new pending bubble into
    // view. A failure grows the bubble (error + Retry), so show that too.
    const pendingCount = pendingUploads.length + outbox.length;
    const failedCount =
      pendingUploads.filter((upload) => upload.status === 'failed').length +
      outbox.filter((entry) => entry.status === 'failed').length;
    const previousPendingRef = useRef({ pendingCount, failedCount });
    useEffect(() => {
      const previous = previousPendingRef.current;
      if (
        pendingCount > previous.pendingCount ||
        failedCount > previous.failedCount
      ) {
        scrollToBottom('smooth');
      }
      previousPendingRef.current = { pendingCount, failedCount };
    }, [pendingCount, failedCount, scrollToBottom]);

    // Infinite scroll up (D270): a sentinel at the top, watched with a
    // margin of one screen, asks for the next older page before the reader
    // gets there. One request at a time (the hook's ref), and not while the
    // first page, the unread search (D229), a jump or a failed page (Try
    // again) is on. The observer is made again after each page, and its
    // first report says whether the sentinel is still in range: a short
    // page that doesn't fill the screen keeps loading.
    const sentinelRef = useRef<HTMLDivElement>(null);
    const onLoadOlderRef = useRef(onLoadOlder);
    onLoadOlderRef.current = onLoadOlder;
    const canLoadOlder =
      hasMore &&
      messages.length > 0 &&
      !loading &&
      !loadingOlder &&
      !searchingUnread &&
      !jumping &&
      !olderError;
    const oldestId = messages[0]?.id ?? null;
    useEffect(() => {
      const list = listRef.current;
      const sentinel = sentinelRef.current;
      if (!canLoadOlder || !list || !sentinel) return;
      if (typeof IntersectionObserver === 'undefined') return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            onLoadOlderRef.current();
          }
        },
        {
          root: list,
          rootMargin: `${Math.max(listHeight || list.clientHeight, 1)}px 0px 0px 0px`,
        }
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [canLoadOlder, oldestId, conversationId, listHeight]);

    // Jump to latest (D271). Far away: jump to a screen above the bottom at
    // once, then settle with a short smooth scroll; near: smooth all the way.
    // Not loaded (a jump far back): load the newest page, which then opens at
    // its bottom. Reaching the bottom marks read as scrolling there does.
    const jumpToLatest = useCallback(async () => {
      const list = listRef.current;
      if (!list) return;
      unreadAnchoredRef.current = false;
      if (hasNewer) {
        if (!onLoadLatest) return;
        followLatestRef.current = true;
        const ok = await onLoadLatest();
        if (!ok) followLatestRef.current = false;
        return;
      }
      nearBottomRef.current = true;
      setNewMessageCount(0);
      setFarFromBottom(false);
      settlingRef.current = true;
      setTimeout(() => {
        if (!settlingRef.current) return;
        settlingRef.current = false;
        measureDistance();
      }, SETTLE_MS);
      const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distance > list.clientHeight * INSTANT_JUMP_SCREENS) {
        list.scrollTo({
          top: list.scrollHeight - list.clientHeight * 2,
          behavior: 'auto',
        });
      }
      scrollToBottom('smooth');
    }, [hasNewer, onLoadLatest, scrollToBottom, measureDistance]);
    jumpToLatestRef.current = jumpToLatest;

    // The button (D271): more than a screen from the bottom, messages
    // arrived below the view, or the window stops short of the newest.
    const jumpCount = newMessageCount + (hasNewer ? newerCount : 0);
    const showJump =
      messages.length > 0 && (hasNewer || farFromBottom || newMessageCount > 0);

    // Some messages here are locked on this device (D137) — or none can be
    // opened because the browser can't keep a device key (D262).
    // One that can’t be verified (D327, D347) isn’t waiting for a key.
    const anyLocked = messages.some(
      (message) => message.e2ee?.locked && !message.e2ee.unverified
    );
    const keyStore = useKeyStorageState();

    return (
      <Box
        flex="1"
        minH={0}
        position="relative"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        {keyStore !== 'ok' ? (
          <NewDeviceBanner variant={keyStore} />
        ) : (
          anyLocked && <NewDeviceBanner />
        )}
        {/* The first page's mark sits over the empty list, never among the
            rows: the moment messages land it goes, so the scroll maths and
            the unread divider (D229) are untouched (D248). */}
        {firstPage.show && messages.length === 0 && (
          <Center
            position="absolute"
            inset={0}
            zIndex={1}
            bg="canvas"
            data-testid="first-page-loading"
          >
            <LoadingPanel
              state={firstPage}
              label={t('common.loading.messages')}
            />
          </Center>
        )}
        <VStack
          ref={listRef}
          data-testid="message-list"
          onScroll={handleScroll}
          onWheel={releaseUnreadAnchor}
          onTouchStart={releaseUnreadAnchor}
          onPointerDown={releaseUnreadAnchor}
          onKeyDown={releaseUnreadAnchor}
          flex="1"
          minH={0}
          overflowY="auto"
          overflowX="hidden"
          // A 16px gutter plus the notch, on both sides (D257).
          ps="calc(16px + env(safe-area-inset-left, 0px))"
          pe="calc(16px + env(safe-area-inset-right, 0px))"
          pt={2}
          pb={6}
          // Rows set their own spacing: 2px inside a run, 12px between (D258).
          spacing={0}
          align="stretch"
          // Contain the scroll so it never drags the page or triggers
          // pull-to-refresh; anchoring is handled by hand on prepend.
          sx={{ overscrollBehaviorY: 'contain', overflowAnchor: 'none' }}
        >
          {olderFailed.failed && hasMore ? (
            // An older page failed: Try again, never a retry loop (D270).
            <LoadingPanel
              state={olderFailed}
              size="inline"
              minH={`${TOP_AREA_PX}px`}
            />
          ) : hasMore || loadingOlder || searchingUnread || jumping ? (
            // The sentinel (D270): watched a screen ahead of the reader.
            <Center
              ref={sentinelRef}
              data-testid="older-sentinel"
              py={2}
              minH={`${TOP_AREA_PX}px`}
            >
              {topWait.loader && (
                // Quiet: only the first page's region announces (D251).
                <TwindenLoader size="inline" announce={false} />
              )}
            </Center>
          ) : (
            messages.length > 0 && (
              <Text fontSize="xs" color={subtle} textAlign="center" py={2}>
                {t('chat.list.start')}
              </Text>
            )
          )}

          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const next = messages[index + 1];
            const date = new Date(message.createdAt);
            const newDay =
              !previous || !isSameDay(new Date(previous.createdAt), date);
            const noticesHere = notices.before.get(message.id) ?? [];

            // The unread divider (D229), under the day chip when it starts a day.
            const unreadDivider = unreadMarker?.messageId === message.id && (
              <Flex
                key={`unread-${message.id}`}
                id="unread-divider"
                role="separator"
                align="center"
                gap={3}
                py={3}
              >
                <Box flex="1" h="1px" bg="line" />
                <Text
                  fontSize="13px"
                  fontWeight={700}
                  color="accent"
                  bg="accent.tint"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {unreadMarker.count === 1
                    ? t('chat.unread.one')
                    : t('chat.unread.other', { n: unreadMarker.count })}
                </Text>
                <Box flex="1" h="1px" bg="line" />
              </Flex>
            );

            const daySeparator = newDay && (
              <Flex key={`day-${message.id}`} justify="center" py={2}>
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  color={subtle}
                  bg={dayChipBg}
                  px={3}
                  py={0.5}
                  borderRadius="full"
                >
                  {formatDayLabel(date)}
                </Text>
              </Flex>
            );

            // Call summaries are pills with "Call back" (Living Room calls).
            const callMeta = callMetadata(message);
            if (callMeta) {
              return [
                daySeparator,
                ...noticesHere.map(noticeLine),
                unreadDivider,
                <Flex key={message.id} justify="center">
                  <CallSummary
                    message={message}
                    isOwn={Boolean(
                      currentUserId && message.sender?.id === currentUserId
                    )}
                    meta={callMeta}
                  />
                </Flex>,
              ];
            }

            // Other system events render as centred muted text, on no side
            // (D259).
            if (message.type === 'system') {
              return [
                daySeparator,
                ...noticesHere.map(noticeLine),
                unreadDivider,
                <Flex
                  key={message.id}
                  data-testid="system-line"
                  justify="center"
                  py={1}
                >
                  <Text fontSize="xs" color={subtle} textAlign="center">
                    {systemMessageText(message)}
                  </Text>
                </Flex>,
              ];
            }

            // The unread divider (D229) breaks a run too, so the first
            // unread message has its name and full corners (D258).
            const isFirstInRun =
              newDay ||
              unreadMarker?.messageId === message.id ||
              noticesHere.length > 0 ||
              startsRun(previous, message);
            const isLastInRun =
              !next ||
              !isSameDay(date, new Date(next.createdAt)) ||
              unreadMarker?.messageId === next.id ||
              notices.before.has(next.id) ||
              startsRun(message, next);
            // Mine on the right, everyone else's on the left (M68, D257).
            const side =
              currentUserId && message.sender?.id === currentUserId
                ? 'mine'
                : 'theirs';

            return [
              daySeparator,
              ...noticesHere.map(noticeLine),
              unreadDivider,
              <MessageBubble
                key={message.id}
                message={message}
                side={side}
                isFirstInRun={isFirstInRun}
                isLastInRun={isLastInRun}
                currentUserId={currentUserId}
                familyId={familyId}
                otherParticipantIds={otherParticipantIds}
                isGroup={isGroup}
                handleById={handleById}
                highlighted={highlightId === message.id}
                actions={actions}
                onMediaLoad={handleMediaLoad}
              />,
            ];
          })}
          {notices.after.map(noticeLine)}
          <PendingBand entries={outbox} uploads={pendingUploads} />
        </VStack>

        {showJump && (
          // Jump to latest (D271): bottom-right of the list, so above the
          // composer and above the emoji panel when it's open (the list's
          // box ends there); clear of the notch on the right.
          <Box
            position="absolute"
            bottom={3}
            right="calc(12px + env(safe-area-inset-right, 0px))"
            zIndex={1}
          >
            <IconButton
              data-testid="jump-to-latest"
              aria-label={
                jumpCount === 0
                  ? t('chat.list.jumpLatest')
                  : jumpCount === 1
                    ? t('chat.list.jumpLatestOne')
                    : t('chat.list.jumpLatestOther', { n: jumpCount })
              }
              icon={<ArrowDown size={22} aria-hidden />}
              isRound
              w="44px"
              h="44px"
              minW="44px"
              bg="surface.raised"
              color="ink"
              borderWidth="1px"
              borderColor="line"
              boxShadow="md"
              _hover={{ bg: pressedBg }}
              _active={{ bg: pressedBg }}
              isLoading={loadingLatest}
              onClick={() => void jumpToLatest()}
            />
            {jumpCount > 0 && (
              <Box
                aria-hidden
                data-testid="jump-to-latest-badge"
                position="absolute"
                top="-6px"
                right="-4px"
                minW="20px"
                h="20px"
                px="5px"
                borderRadius="full"
                bg="accent.solid"
                color="accent.onSolid"
                fontSize="11px"
                fontWeight={700}
                lineHeight="20px"
                textAlign="center"
                pointerEvents="none"
              >
                {jumpCount > 99 ? '99+' : jumpCount}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }
);
